import {
  ChangeEvent,
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { OrganisationContext } from '../../../context';
import { locationApi, PlaceItem } from '../../../services';
import { useDebounce, useOnClickOutside } from '../../hooks';
import { PaginationInfo } from '../../models';
import { DropdownMenu } from '../DropdownMenu';
import { InputField } from '../InputField';
import { Popup } from '../Popup';

import dropdownMenuClasses from '../../components/DropdownMenu/DropdownMenu.module.scss';
import classes from './LocationInputField.module.scss';

interface LocationInputFieldProps {
  value: string;
  label?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const LocationInputField: FunctionComponent<LocationInputFieldProps> = memo(
  ({ value, label, disabled, autoFocus, onChange }) => {
    const { t } = useTranslation();

    const [locationPlacesSearch] = locationApi.endpoints.locationPlacesSearch.useLazyQuery();

    const { locationPlacesSearchEnabled } = useContext(OrganisationContext).organisation.settings;

    const [loading, setLoading] = useState(false);

    const [showPlaces, setShowPlaces] = useState<boolean>(false);

    const [places, setPlaces] = useState<PlaceItem[]>([]);

    const [pageInfo, setPageInfo] = useState<PaginationInfo>({
      page: 1,
      hasNextPage: false,
    } as PaginationInfo);

    const { page, hasNextPage } = pageInfo;

    const debounced = useDebounce<string>(value);

    const containerRef = useRef<HTMLDivElement>(null);

    useOnClickOutside(containerRef, () => setShowPlaces(false));

    const fetchPlaces = useCallback(async () => {
      setLoading(true);

      const response = await locationPlacesSearch({ searchText: debounced }).unwrap();

      const { items = null, pageInfo = null } = { ...response };

      items && setPlaces(items);

      pageInfo && setPageInfo(pageInfo);

      setLoading(false);
    }, [debounced, locationPlacesSearch]);

    useEffect(() => {
      fetchPlaces();
    }, [debounced, fetchPlaces]);

    const loadMore = useCallback(() => {
      locationPlacesSearch({ searchText: debounced, page: page + 1 }).then(({ data }) => {
        if (!data) {
          return;
        }

        const { items, pageInfo } = data;

        setPlaces([...places, ...items]);

        setPageInfo(pageInfo);
      });
    }, [debounced, locationPlacesSearch, page, places]);

    const renderPlaceItems = useMemo(() => {
      if (loading) {
        return <div className={classes['location-input__loading']}>{t('common.loading')}</div>;
      }

      if (!places.length) {
        return <div className={classes['location-input__empty']}>{t('common.nothing-found')}</div>;
      }

      const onClickHandler = (value: string) => {
        onChange({ target: { value } } as ChangeEvent<HTMLInputElement>);
        setShowPlaces(false);
      };

      return (
        <InfiniteScroll
          className={classes['location-input__infinite-scroll']}
          hasMore={hasNextPage}
          next={loadMore}
          loader={<Skeleton height={'3rem'} />}
          {...((page !== 1 || hasNextPage) && { height: '13.5rem' })}
          dataLength={places.length}
          hasChildren
        >
          {places.map(({ formattedAddress }, index) => {
            return (
              <div
                key={`search-result-${index}`}
                className={dropdownMenuClasses['dropdown-menu__item']}
                onClick={() => onClickHandler(formattedAddress)}
              >
                {formattedAddress}
              </div>
            );
          })}
        </InfiniteScroll>
      );
    }, [hasNextPage, loadMore, loading, onChange, page, places, t]);

    if (!locationPlacesSearchEnabled) {
      return (
        <InputField
          value={value}
          label={label}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={onChange}
        />
      );
    }

    return (
      <div ref={containerRef} className={classes['location-input']}>
        <InputField
          value={value}
          label={label}
          onChange={onChange}
          onFocus={() => setShowPlaces(true)}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <Popup
          isOpen={showPlaces}
          bodyBottom={'3.5rem'}
          bodyLeft={'0.5rem'}
          bodyWidth={'calc(100% - 1rem)'}
          body={<DropdownMenu content={renderPlaceItems} />}
        />
      </div>
    );
  }
);
